import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, FileWarning, ShieldCheck, HelpCircle, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { epsteinPlaceDossier } from "../lib/api";
import { RecordMediaGallery } from "../components/media/PublishedMedia";

/**
 * EpsteinDossier — public, read-only structured dossier for a place / property
 * entity. Only renders sections whose `section_status === "approved"` and whose
 * parent entity is `public=true`. Otherwise the API returns 404 and the user
 * sees the standard not-found state — i.e. nothing is leaked before approval.
 */

const EVIDENCE_CLASS_META = {
  verified_fact:        { label: "VERIFIED FACT",        color: "#5DD4B3", icon: ShieldCheck },
  reported_allegation:  { label: "REPORTED ALLEGATION",  color: "#E0A526", icon: FileWarning },
  open_question:        { label: "OPEN QUESTION",        color: "#9FA8D4", icon: HelpCircle },
  context_only:         { label: "CONTEXT ONLY",         color: "#A39E93", icon: BookOpen },
};

const EpsteinDossier = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await epsteinPlaceDossier(slug);
        if (active) setData(r);
      } catch (e) {
        toast.error("Dossier not available.");
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center font-mono text-[#757065]" data-testid="ef-dossier-loading">
        // LOADING DOSSIER…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex flex-col items-center justify-center gap-3" data-testid="ef-dossier-404">
        <div className="font-mono text-xs text-[#757065]">// DOSSIER NOT AVAILABLE</div>
        <div className="font-mono text-[10px] text-[#5a544a] max-w-sm text-center px-4">
          The dossier you requested is either still in editorial review or not yet released to the public archive.
        </div>
        <Link to="/epstein-files" className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
          ← back to the file
        </Link>
      </div>
    );
  }

  return <DossierRenderer data={data} />;
};

// =============================================================================
// Renderer — also used by AdminDossierEditor preview pane
// =============================================================================
export const DossierRenderer = ({ data, includeDraftSections = false }) => {
  const { entity, sections, sources } = data;
  const sourceMap = useMemo(() => {
    const m = {};
    (sources || []).forEach((s) => { m[s.id] = s; });
    return m;
  }, [sources]);

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-[#E4DFD3]" data-testid={`ef-dossier-page-${entity.slug}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-10">
        {/* Top nav */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Link
            to={`/epstein-files/entity/${entity.slug}`}
            data-testid="ef-dossier-back-entity"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20]"
          >
            <ArrowLeft size={12} /> Entity card
          </Link>
          <span className="text-[#454038] font-mono text-[10px]">·</span>
          <Link
            to="/epstein-files"
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20]"
          >
            Evidence board
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#D92D20] border border-[#D92D20] px-2 py-0.5">
              // PROPERTY DOSSIER
            </span>
            {entity.category_label && (
              <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#A39E93] border border-[#454038] px-2 py-0.5">
                {entity.category_label}
              </span>
            )}
          </div>

          {/* Expanded image slot */}
          {(entity.image_url || entity.photo_url || entity.thumbnail_url) && (
            <figure
              className="mb-4 border border-[#2D2A26] bg-[#0a0907]"
              data-testid="ef-dossier-image"
            >
              <div className="aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
                <img
                  src={entity.image_url || entity.photo_url || entity.thumbnail_url}
                  alt={entity.image_alt || entity.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {entity.image_source && (
                <figcaption className="px-3 py-1.5 border-t border-[#2D2A26] font-mono text-[9px] uppercase tracking-[0.3em] text-[#7a7062]">
                  // source · {entity.image_source}
                </figcaption>
              )}
            </figure>
          )}

          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#E4DFD3] leading-[0.95] mb-3"
            data-testid="ef-dossier-title"
          >
            {entity.name}
          </h1>
          {entity.summary && (
            <p className="max-w-3xl text-[14px] text-[#CBB892] leading-relaxed border-l-2 border-[#D92D20] pl-3 italic">
              {entity.summary}
            </p>
          )}
          <RecordMediaGallery targetType="any" targetId={entity.slug} />
        </div>

        {/* Sections */}
        <div className="space-y-10">
          {sections.map((s, i) => (
            <DossierSection
              key={`${s.heading}-${i}`}
              section={s}
              sourceMap={sourceMap}
              isDraftBadge={includeDraftSections && s.section_status !== "approved"}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 border-t border-[#2D2A26] pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065]">
            // EOF · DOSSIER · {entity.slug}
          </div>
          <div className="font-mono text-[10px] text-[#7a7062]">
            Editorial standard: 100% source-backed public records · No guilt by association
          </div>
        </div>
      </div>
    </div>
  );
};

const DossierSection = ({ section, sourceMap, isDraftBadge }) => {
  const meta = EVIDENCE_CLASS_META[section.evidence_class] || EVIDENCE_CLASS_META.context_only;
  const Icon = meta.icon;
  const sectionSources = (section.sources || [])
    .map((sid) => sourceMap[sid])
    .filter(Boolean);
  return (
    <section
      data-testid={`ef-dossier-section-${section.idx}`}
      className="border-l-2 border-[#2D2A26] hover:border-[#D92D20]/60 transition-colors pl-4 sm:pl-5"
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span
          className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.35em] px-2 py-0.5 border"
          style={{ color: meta.color, borderColor: `${meta.color}55` }}
        >
          <Icon size={10} /> {meta.label}
        </span>
        {isDraftBadge && (
          <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#E0A526] border border-[#E0A526]/40 px-2 py-0.5">
            DRAFT
          </span>
        )}
      </div>
      <h2 className="font-display text-2xl sm:text-3xl text-[#E4DFD3] leading-tight mb-3">
        {section.heading}
      </h2>
      {section.body_md ? (
        <div
          className="prose-dossier text-[14px] text-[#CBB892] leading-relaxed whitespace-pre-wrap break-words max-w-3xl"
          data-testid={`ef-dossier-section-body-${section.idx}`}
        >
          {section.body_md}
        </div>
      ) : (
        <p className="text-[13px] italic text-[#7a7062]">
          // SECTION BODY NOT YET PUBLISHED
        </p>
      )}

      {sectionSources.length > 0 && (
        <div className="mt-4">
          <div className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#7a7062] mb-1.5">
            // SOURCES
          </div>
          <ul className="space-y-1">
            {sectionSources.map((src) => (
              <li key={src.id} className="flex items-start gap-2 font-mono text-[11px] text-[#A39E93]">
                <span className="text-[#5a544a]">·</span>
                <a
                  href={src.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#D92D20] inline-flex items-center gap-1"
                >
                  <span className="truncate max-w-[60ch]">{src.title || src.url}</span>
                  <ExternalLink size={10} className="shrink-0" />
                </a>
                {src.publisher && (
                  <span className="text-[#5a544a]">— {src.publisher}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default EpsteinDossier;
